import React from 'react';
import { useEffect, useState } from 'react';
import Loading from '../Loading';
import { OptionKeys, Product } from '../../../cart/@types';
import { SelectedOption, VariantsEntity } from '../../@types';
import './index.css';
import Popup from '../Popup';
import { fetchCart, updateIconCount } from '../../utils';

interface Props {
  productVariantIds: string[];
  cartIconClass: string | undefined;
  shop: string;
  productTags: string[];
  variantId: string;
  productHandle: string;
  hiddenClasses: string[];
  productImageSwatches: string[];
  shopCurrency: string;
}
interface PositionTag {
  items: Product;
  name: string;
}
interface TagPosition {
  [tagName: string]: string;
}
const api =
  process.env.NODE_ENV !== 'development'
    ? 'https://asia-southeast1-enda-combine-stock-shopify-app.cloudfunctions.net'
    : process.env.NGROK_DEV;

const AddonModule = (props: Props) => {
  const {
    productVariantIds,
    shop,
    productTags,
    variantId,
    hiddenClasses,
    productHandle,
    cartIconClass,
    productImageSwatches,
    shopCurrency,
  } = props;
  const [product, setProduct] = useState<Product | null>(null);
  const [personalisationId, setPersonalisationId] = useState((Math.random() * 10000).toFixed(0));
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState<Product[]>([]);
  const [tagPosition, setTagPosition] = useState<PositionTag[]>([]);
  const [variantProducts, setVariantProducts] = useState({} as { [key: string]: Product[] });
  const [selectedOptions, setSelectedOptions] = useState<{ [index: number]: SelectedOption }>({});
  const [selectedAddonProducts, setSelectedAddonProducts] = useState<
    ({
      variantId: number;
      productId: number;
    } | null)[]
  >([]);
  const [selectedAddon, setSelectedAddon] = useState<{ [key: string]: boolean }>({});
  const [customTextInputsValue, setCustomTextInputsValue] = useState<{
    [productIndex: string]: { [key: string]: string } | undefined;
  }>({});
  // { 0: { 'Wish Text': 'hello' } }
  const [qty, setQty] = useState(1);
  const [busy, setBusy] = useState(false);
  const [popupData, setPopupData] = useState(null);

  // todo: check for inventory
  /*useEffect(() => {
    const loadProduct = async () => {
      try {
        const response = await fetch(`/products/${productHandle}.js`);
        const data = await response.json();
        console.log('data', data);
        setProduct(data);
      } catch (error) {
        console.log(error);
      }
    };
    loadProduct();
  }, [productHandle]);*/

  const addonProducts = products.concat(variantProducts[variantId] || []).filter((e) => e);

  const swatchMap = productImageSwatches.reduce((acc, curr) => {
    const colonIndex = curr.indexOf(':');
    const key = curr.substring(0, colonIndex).trim();
    const value = curr.substring(colonIndex + 1).trim();
    acc[key] = value;
    return acc;
  }, {} as { [key: string]: string });

  const shopCurrencyValue = shopCurrency;

  const handleCustomTextInputChange = (productIndex: number, key: string, value: string) => {
    setCustomTextInputsValue((prev) => ({
      ...prev,
      [productIndex]: {
        ...(prev[productIndex] || {}),
        [key]: value,
      },
    }));
  };

  useEffect(() => {
    if (loading || addonProducts.length > 0) {
      hiddenClasses.forEach((className) => {
        const elements = document.getElementsByClassName(className);
        Array.from(elements).forEach((element) => {
          element.setAttribute('style', 'display: none !important');
        });
      });
    } else {
      hiddenClasses.forEach((className) => {
        const elements = document.getElementsByClassName(className);
        Array.from(elements).forEach((element) => {
          element.setAttribute('style', '');
        });
      });
    }
  }, [loading, hiddenClasses, addonProducts]);

  // check if plain hard box is selected
  const isSpecificRadioSelected = (productIndex: number) => {
    const specificId = 'Plain Hard Box with Wax Seal and Ribbon';
    const selectedOption = selectedOptions[productIndex];
    // console.log(specificId)
    // console.log(selectedOption)
    return selectedOption && Object.values(selectedOption).includes(specificId);
  };

  // conditional custom input
  const hideCustomTextInput = (productIndex: number) => {
    return isSpecificRadioSelected(productIndex) || !selectedAddon[productIndex];
  };

  const handleAddToCart = async () => {
    setBusy(true);
    try {
      const addonProducts = selectedAddonProducts
        .map((product, index) => {
          if (!product) return null;
          const customProperties = customTextInputsValue[index];
          return {
            id: product!.variantId,
            quantity: 1,
            properties: customProperties
              ? {
                  Personalisation: personalisationId,
                  Addon: 'Yes',
                  ...customProperties,
                }
              : {
                  Personalisation: personalisationId,
                  Addon: 'Yes',
                },
          };
        })
        .filter((product) => product !== null);
      const items = [
        {
          id: variantId,
          quantity: qty,
          properties: {
            Personalisation: personalisationId,
          },
        },
        ...addonProducts,
      ];
      const resp = await fetch(`/cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ items }),
      });

      const data = await resp.json();
      setPopupData(data);
      const cart = await fetchCart();
      updateIconCount(cart, cartIconClass);
    } catch (err) {
      console.log(err);
    } finally {
      setPersonalisationId((Math.random() * 10000).toFixed(0));
      setBusy(false);
    }
  };

  const handleClosePopup = () => {
    setPopupData(null);
  };

  useEffect(() => {
    // set selected combo products based on selected options
    const selectedAddonProducts: { variantId: number; productId: number }[] = new Array(
      products.length,
    ).fill(null);
    tagPosition.forEach((product, productIndex) => {
      // select first variant if there's just one option
      if (product.items.variants?.length === 1) {
        selectedAddonProducts[productIndex] = {
          variantId: product.items.variants[0].id,
          productId: product.items.id,
        };
        return;
      }
      const selectedOption = selectedOptions[productIndex];
      const selectedVariants =
        (selectedOption &&
          product.items.variants?.filter((variant) => {
            return Object.keys(selectedOption).every((key) => {
              return selectedOption[key as OptionKeys] === variant[key as OptionKeys];
            });
          })) ||
        [];
      if (selectedVariants.length === 1) {
        selectedAddonProducts[productIndex] = {
          variantId: selectedVariants[0].id,
          productId: product.items.id,
        };
      }
    });
    setSelectedAddonProducts(selectedAddonProducts);
  }, [selectedOptions, setSelectedAddonProducts]);

  useEffect(() => {
    const fetchAddon = async () => {
      setLoading(true);
      try {
        const response = await fetch(
          `${api}/getProductAddons2?variant_ids=${productVariantIds.join(
            ',',
          )}&product_tags=${productTags.join(',')}&shop=${shop}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
          },
        );
        const {
          products: p,
          variantProducts: vp,
          tagPosition: tp,
        } = (await response.json()) as {
          products: Product[];
          variantProducts: { variantId: string; addonProducts: Product[] }[];
          tagPosition: { [tagName: string]: string }[];
        };

        const products = p;
        const tagPosition = tp;
        let sortedTags = [] as any[];

        tagPosition.map((tp) => {
          products.map((p) => {
            if (Object.values(tp)[0] === p.id.toString()) {
              sortedTags.push({
                name: Object.keys(tp)[0],
                items: p,
              });
            }
          });
        });
        setTagPosition(sortedTags);

        // console.log("sorted", sortedTags)
        const variantProducts = vp.reduce((acc, curr) => {
          acc[curr.variantId] = curr.addonProducts;
          return acc;
        }, {} as { [key: string]: Product[] });

        setProducts(products);

        setVariantProducts(variantProducts);
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    };
    fetchAddon();
  }, []);

  if (loading)
    return (
      <div className="rp-container">
        <Loading />
      </div>
    );
  if (addonProducts.length === 0) return null;

  const handleSelectOption = (
    productIndex: number,
    optionName: OptionKeys,
    optionValue: string,
  ) => {
    // check if already checked
    const selectedOption = selectedOptions[productIndex];
    if (selectedOption && selectedOption[optionName] === optionValue) {
      setSelectedOptions((prev) => {
        const selectedOption = { ...prev[productIndex] };
        delete selectedOption[optionName];
        return {
          ...prev,
          [productIndex]: selectedOption,
        };
      });
      return;
    } else {
      setSelectedOptions((prev) => ({
        ...prev,
        [productIndex]: {
          ...prev[productIndex],
          [optionName]: optionValue,
        },
      }));
    }
  };

  const handleSelectAddon = (productIndex: number, addAddon: 'yes' | 'no') => {
    setSelectedAddon((prev) => ({
      ...prev,
      [productIndex]: addAddon === 'yes',
    }));
  };

  const isAllowedCheckout = addonProducts.every((e, i) =>
    !selectedAddon[i] ? true : selectedAddonProducts[i],
  );

  const isVariantOutOfStock = (variant: VariantsEntity) => {
    return variant.inventory_quantity === 0 && variant.inventory_policy === 'deny';
  };

  return (
    <div className="rp-container">
      {tagPosition.map((product, productIndex) => {
        const getPriceOrRange = (selectedOptions: SelectedOption | null) => {
          const formatPrice = (price: number, currency: string) => {
            if (currency === 'Rp') {
              // currency Rp, remove 2 decimal places.
              return price.toLocaleString(undefined, {
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              });
            } else {
              // Other currencies, format with 2 decimal places.
              return price.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              });
            }
          };

          if (product.items.variants?.length === 1) {
            const price = parseFloat(product.items.variants[0].price);
            return shopCurrencyValue + ' ' + formatPrice(price, shopCurrencyValue);
          }

          const prices = (product.items.variants || [])
            .filter((variant) => {
              return Object.keys(selectedOptions || {}).every((key) => {
                return (selectedOptions || {})[key as OptionKeys] === variant[key as OptionKeys];
              });
            })
            .map((variant) => parseFloat(variant.price));

          if (prices.every((price) => price === prices[0])) {
            return shopCurrencyValue + ' ' + formatPrice(prices[0], shopCurrencyValue);
          } else {
            const minPrice = Math.min(...prices);
            const maxPrice = Math.max(...prices);
            return `${shopCurrencyValue}${formatPrice(
              minPrice,
              shopCurrencyValue,
            )} - ${shopCurrencyValue}${formatPrice(maxPrice, shopCurrencyValue)}`;
          }
        };
        const price = getPriceOrRange(selectedOptions[productIndex]);
        const productTags = product.items.tags.split(', ');
        const customTextInputs = productTags
          .filter((tag) => tag.startsWith('_cp'))
          .map((tag) => {
            const [, inputCharLength, ...inputTitle] = tag.split(' ');
            const title = inputTitle.join(' ');
            return {
              inputCharLength: inputCharLength ? parseInt(inputCharLength) : 100,
              title,
            };
          });
        const isOutOfStock = product.items.variants?.every(isVariantOutOfStock);
        let imageSrc = product.items.image.src; // default image when no variant selected

        // console.log({product, selectedComboProduct})
        let imageProduct;
        if (selectedOptions[productIndex]) {
          const productColor = selectedOptions[productIndex].option1;

          const variantProduct = product.items.variants?.find(
            (variant) => variant.option1 === productColor,
          );

          if (variantProduct) {
            imageProduct = product.items.images?.find(
              (image) => image.id === variantProduct.image_id,
            );
          }
        }
        return (
          <div className="rp-card" key={product.items.id + productIndex}>
            <div className="rp-title-container">
              <div className="flex items-center">
                <img
                  src={imageProduct?.src ? imageProduct?.src : product.items.image.src}
                  style={{ width: '13%', marginRight: '10px', borderRadius: '3px' }}
                />
                <div>
                  <span>{product.items.title}</span>
                </div>
              </div>
              <span className={`rp-price${isOutOfStock ? ' oos' : ''}`}>
                {isOutOfStock ? 'Out of Stock' : `+ ${price}`}
              </span>
            </div>
            <div style={{ paddingBottom: '10px' }}>
              {product.items.title === 'Add-On - Wrapping Service' ? (
                <span>
                  **This service includes wrapping only.
                  <a href="https://sitikhadijah.com/collections/box" target="_blank">
                    {' '}
                    Click here for box options.
                  </a>
                  <br />
                </span>
              ) : null}
            </div>
            <div className="rp-card-option">
              <div className="rp-card-option-values-container-yn">
                <div className="rp-option-item">
                  <label>
                    <input
                      type="radio"
                      id={productIndex + product.items.id + 'yes'}
                      name={productIndex + ''}
                      value="yes"
                      checked={selectedAddon[productIndex]}
                      onClick={(e) => handleSelectAddon(productIndex, 'yes')}
                    />
                    Yes
                  </label>
                </div>
                <div className="rp-option-item">
                  <label>
                    <input
                      type="radio"
                      id={productIndex + product.items.id + 'no'}
                      name={productIndex + ''}
                      value="no"
                      checked={!selectedAddon[productIndex]}
                      onClick={(e) => handleSelectAddon(productIndex, 'no')}
                    />
                    No
                  </label>
                </div>
              </div>
            </div>
            {(product.items.variants?.length === 1 && customTextInputs.length === 0) ||
            !selectedAddon[productIndex] ? null : (
              <div className="rp-card-option-container">
                {product.items.options?.map((option, index) => {
                  if (product.items.variants?.length === 1) return null;
                  const optionValue =
                    selectedOptions[productIndex]?.[`option${option.position}` as OptionKeys];
                  return (
                    <div className="rp-card-option" key={option.id}>
                      <div>
                        <span className="rp-option-name">{option.name}</span>
                        <span className="rp-option-value">
                          {optionValue ? `: ${optionValue}` : null}
                        </span>
                      </div>
                      <div className="rp-card-option-values-container">
                        {option.values?.map((value) => {
                          const possibleVariants = (product.items.variants || []).filter(
                            (variant) => {
                              // check if variant is available if selected
                              const futureSelectedOptions = {
                                ...selectedOptions[productIndex],
                                [`option${option.position}` as OptionKeys]: value,
                              };
                              return Object.keys(futureSelectedOptions).every((key) => {
                                return (
                                  futureSelectedOptions[key as OptionKeys] ===
                                  variant[key as OptionKeys]
                                );
                              });
                            },
                          );

                          // function to check inventory for sale with considering selected combo products
                          const inventoryForSale = (variant: VariantsEntity) => {
                            const selectedVariants = selectedAddonProducts.filter(
                              (e) => e?.variantId === variant.id,
                            );
                            if (variant.inventory_management === null) return 999;
                            if (variant.inventory_policy !== 'deny') return 999;
                            return variant.inventory_quantity - selectedVariants.length;
                          };

                          // check if all possible variants are out of stock
                          const isDisabled = possibleVariants.every((variant) => {
                            return inventoryForSale(variant) === 0 || inventoryForSale(variant) < 0;
                          });

                          return (
                            <div
                              className={
                                swatchMap[value]
                                  ? 'rp-option-item rp-image-swatches'
                                  : 'rp-option-item'
                              }
                              key={value}
                              data-swatch={value}
                              style={
                                swatchMap[value]
                                  ? { backgroundImage: `url(${swatchMap[value]})` }
                                  : undefined
                              }
                            >
                              <label>
                                <input
                                  disabled={isDisabled}
                                  type="radio"
                                  id={productIndex + product.items.id + value}
                                  name={productIndex + option.name}
                                  value={value}
                                  checked={optionValue === value}
                                  onClick={(e) =>
                                    handleSelectOption(
                                      productIndex,
                                      `option${option.position}` as OptionKeys,
                                      value,
                                    )
                                  }
                                />
                                {swatchMap[value] ? '' : value}
                              </label>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}

                {hideCustomTextInput(productIndex)
                  ? null
                  : customTextInputs.map((input, index) => (
                      <div className="rp-card-option" key={index}>
                        <div>
                          <span className="rp-option-name">{input.title}</span>
                        </div>
                        <div className="rp-card-option-values-container">
                          <textarea
                            className="rp-card-option-value-input-container"
                            placeholder={`Max: ${input.inputCharLength} characters`}
                            maxLength={input.inputCharLength}
                            value={(customTextInputsValue[productIndex] || {})[input.title]}
                            onChange={(e) =>
                              handleCustomTextInputChange(productIndex, input.title, e.target.value)
                            }
                          />
                        </div>
                      </div>
                    ))}
              </div>
            )}
          </div>
        );
      })}

      <div className="rp-quantity-container">
        <p>Quantity</p>
        <div className="rp-quantity-buttons-container">
          <button
            className="rp-quantity-button-input"
            onClick={() => {
              if (qty > 1) setQty(qty - 1);
            }}
          >
            -
          </button>
          <div>
            <span>{qty}</span>
          </div>
          <button
            className="rp-quantity-button-input"
            onClick={() => {
              if (qty < 999) setQty(qty + 1);
            }}
          >
            +
          </button>
        </div>
      </div>

      {popupData && <Popup data={popupData} onClose={handleClosePopup} />}

      <div className="rp-add-button intan-add-button">
        <button
          className="button altcolour btn--secondary"
          disabled={!isAllowedCheckout}
          onClick={handleAddToCart}
        >
          Add to Cart
        </button>
      </div>
    </div>
  );
};

export default AddonModule;
